/**
 * What the Outcomes tab measures (design salesVals L20466-20528): the five portfolio
 * figures and the proof sections, with each metric's label and the line it shows until
 * it is measured. This is product copy. The figures themselves come from the server
 * (GET /sales/outcomes), keyed by `key`; nothing here is a value.
 */
import type { OutcomesPortKey } from '@aithora/shared';

/** [key, label, the line under it until measured] */
export const PORTFOLIO: [OutcomesPortKey, string, string][] = [
  ['collected_total', 'Reimbursement collected', 'Billed by the practitioner, paid on claims'],
  ['live_or_pilot', 'Live or in pilot', 'Facilities live on Aithora'],
  ['days_live', 'Days live', 'Per facility, from start dates'],
  ['enrolled', 'Enrolled', 'And how many consented'],
  ['first_dollar', 'First dollar', 'Go-live to first paid claim'],
];

export interface ProofMetric {
  key: string;
  k: string;
  /** The line shown until the figure is measured. */
  def: string;
  /** A metric not wired yet reads "Not measured yet" rather than "At first go-live". */
  isNew?: boolean;
}
export interface ProofSection {
  title: string;
  q: string;
  rows: ProofMetric[];
}

const m = (key: string, k: string, def: string, isNew = false): ProofMetric => ({ key, k, def, ...(isNew ? { isNew } : {}) });

export const PROOF: ProofSection[] = [
  {
    title: 'Engagement',
    q: 'Is Atlas used?',
    rows: [
      m('residents_active_daily', 'Residents active each day', 'Share of enrolled, daily'),
      m('conversations_per_resident', 'Conversations per resident', 'Per active resident, daily'),
      m('total_conversations', 'Total conversations', 'Since first go-live'),
      m('active_week_vs_target', 'Active this week vs 80% target', 'Share of enrolled active in 7 days', true),
    ],
  },
  {
    title: 'Safety',
    q: 'Does it keep people safe?',
    rows: [
      m('injurious_falls', 'Residents with an injurious fall', '% of residents, vs 3.4% CMS long-stay'),
      m('hospital_visits', 'Hospital visits', 'Per 1,000 resident-days'),
      m('hospital_return_30d', 'Hospital return within 30 days', 'Needs hospital discharge dates', true),
      m('urgent_alerts_real', 'Urgent alerts that were real', 'Share confirmed real, with range'),
      m('time_to_ack_resolve', 'Time to acknowledge / resolve', 'Median, per alert'),
      m('alerts_escalations_aps', 'Alerts · escalations · APS reports', 'By type, since go-live'),
    ],
  },
  {
    title: 'Wellbeing',
    q: 'Are residents better off?',
    rows: [
      m('depression_improved', 'Depression improved by half+', 'Of residents screened twice, with range'),
      m('screening_positive', 'Screening positive, first vs latest', 'First screen vs latest'),
      m('isolation', 'Isolation', 'Change since go-live'),
      m('self_harm_flags', 'Self-harm flags raised', 'Counts under 11 are hidden for privacy'),
      m('no_visitor_14d', 'No visitor in 14 days', 'Share of residents, first month vs latest'),
    ],
  },
  {
    title: 'Before vs after',
    q: '90 days before go-live vs since',
    rows: [
      m('before_after_falls', 'Residents with an injurious fall', 'Needs 90 days of records before go-live'),
      m('before_after_hospital', 'Hospital visits', 'Needs 90 days of records before go-live'),
      m('before_after_response', 'Staff response time', 'Needs 90 days of records before go-live'),
      m('before_after_adherence', 'Medication adherence', 'Needs 90 days of records before go-live'),
      m('before_after_after_hours', 'After-hours alerts answered in 10 min', 'Needs 90 days of records before go-live'),
    ],
  },
  {
    title: 'Care operations',
    q: 'Does care run better?',
    rows: [
      m('medication_adherence', 'Medication adherence', 'Of scheduled doses'),
      m('staff_response_time', 'Staff response time', 'Median call-to-bedside'),
      m('staff_time_saved', 'Staff time saved', 'From signed Atlas Scribe notes'),
      m('handoffs_complete', 'Shift handoffs complete', 'Share of shifts with a complete handoff'),
      m('after_hours_resolved', 'After-hours alerts resolved', 'Share resolved, 7pm–7am'),
    ],
  },
  {
    title: 'Money',
    q: 'What did the documented care earn?',
    rows: [
      m('collected', 'Collected by practitioners', 'Billed by and paid to the enrolled practitioner'),
      m('claims_filed', 'Claims filed', 'Since first go-live'),
      m('still_on_table', 'Still on the table', 'Collectible, and what blocks it'),
    ],
  },
  {
    title: 'Proof quality',
    q: 'Would an investor or CMS trust it?',
    rows: [
      m('predictions_vs_actual', 'Predictions vs what happened', 'Written before each pilot', true),
      m('staggered_adoption', 'Staggered-adoption comparison', 'Needs several live facilities', true),
      m('full_device_days', 'Resident-days with full device data', 'Share of resident-days with complete data'),
      m('ledger_sealed', 'Decisions sealed in the Truth Ledger', 'And the share reviewed by a person'),
      m('figures_callable', 'Figures with enough data to call', 'Rates appear once the sample is large enough'),
    ],
  },
  {
    title: 'Family',
    q: 'Are families involved?',
    rows: [
      m('families_active', 'Families active', 'Portal users, last 30 days'),
      m('portal_logins', 'Portal logins', 'Last 30 days'),
      m('family_messages', 'Messages to the facility', 'Last 30 days'),
      m('photos_shared', 'Photos and memories shared', 'Last 30 days'),
      m('replies_within_day', 'Facility replies within a day', 'Share of family messages'),
    ],
  },
];

/** What unlocks the real results (design ocLocked). */
export const LOCKED: [string, string][] = [
  ['Look-back result', 'Unlocks when a facility’s past records are loaded in Week 1. Labeled as a look-back, never as live.'],
  ['Early read', 'Unlocks at day 30 of a pilot. Rep use only, as a check-in with that same facility.'],
  ['Pilot result', 'Unlocks at day 90, on the metrics agreed in Week 1. Shown to other prospects only if the facility agrees to be a reference.'],
];
